<script setup lang="ts">
import { useSlots, computed } from 'vue';

import { AppSvgIcon } from '@libs/ui';

import { HotelSelectionCardState } from '../models';

interface HotelSelectionCardLayoutProps {
  cardState: HotelSelectionCardState;
  isPreviouslySeen?: boolean;
}

const props = defineProps<HotelSelectionCardLayoutProps>();

const slots = useSlots();
const isSemboBenefitsCard = computed(
  () => !!slots['sembo-benefits-card-title']
);

const isHighlighted = computed(
  () => props.cardState !== HotelSelectionCardState.None
);
</script>

<template>
  <div class="hotel-selection-card-layout">
    <div
      class="hotel-selection-card-wrapper"
      :class="{
        selected: props.cardState === HotelSelectionCardState.Selected,
        suggested: props.cardState === HotelSelectionCardState.Suggested,
        'sembo-benefits-card-wrapper': isSemboBenefitsCard,
      }"
    >
      <div v-if="isSemboBenefitsCard" class="sembo-benefits-card">
        <div class="sembo-benefits-card-title">
          <slot name="sembo-benefits-card-title" />
        </div>
        <div class="sembo-benefits-card-content">
          <slot name="sembo-benefits-card-content" />
        </div>
      </div>
      <template v-else>
        <div class="hotel-selection-card-info">
          <div v-if="isHighlighted" class="selected-label">
            <span>
              {{
                props.cardState === HotelSelectionCardState.Selected
                  ? $t('planner.selected')
                  : $t('planner.included')
              }}
            </span>
          </div>
          <div class="image-wrapper">
            <div v-if="props.isPreviouslySeen" class="previously-seen-label">
              <AppSvgIcon name="eye" color="color-white" size="18" />
              <span>{{ $t('planner.previously_seen') }}</span>
            </div>
            <slot name="image" />
          </div>
          <div class="content-wrapper">
            <slot name="content" />
          </div>
          <div class="summary-wrapper">
            <slot name="summary" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-layout {
  --wide-card-height: 260px;
  --tall-card-height: 510px;

  container-type: inline-size;
  container-name: hotelCard;
  width: 100%;

  .hotel-selection-card-wrapper {
    border-radius: var(--border-radius-12);
    border: 1px solid var(--color-scrollbar);

    overflow: hidden;
    background: var(--color-white);
    height: var(--tall-card-height);

    &.selected {
      border: 1px solid var(--color-neutral-70);
      .selected-label {
        background-color: var(--color-neutral-70);
      }
    }

    &.suggested {
      border: 1px solid var(--color-brand-violet);
      .selected-label {
        background-color: var(--color-brand-violet);
      }
    }

    &.sembo-benefits-card-wrapper {
      background-color: transparent;
      border: none;
      display: flex;
      align-items: center;

      .sembo-benefits-card {
        display: flex;
        flex-direction: column;
        padding: var(--spacing-16) 0;
        gap: var(--spacing-24);
        color: var(--color-neutral-70);
        width: 100%;

        .sembo-benefits-card-content {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-8);
          :deep(.sembo-benefit) {
            padding: var(--spacing-24) var(--spacing-16);
          }
        }
      }
    }

    .hotel-selection-card-info {
      --card-inside-padding: var(--spacing-12);
      position: relative;
      display: flex;
      align-items: center;
      flex-direction: column;
      height: 100%;
      padding: var(--card-inside-padding);
      gap: var(--card-inside-padding);

      .selected-label {
        position: absolute;
        z-index: 1;
        top: 0;
        right: 0;
        border-radius: 0 0 0 var(--border-radius-12);
        padding: var(--spacing-8) var(--spacing-12);
        color: var(--color-white);
        @include font-body-s-light;
      }

      .image-wrapper {
        height: 50%;
        width: 100%;
        position: relative;
        border-radius: var(--border-radius-8);
        overflow: hidden;

        .previously-seen-label {
          position: absolute;
          z-index: 1;
          bottom: 0;
          left: 0;
          border-radius: 0 var(--border-radius-8) 0 0;
          background-color: var(--color-neutral-70);

          display: flex;
          align-items: center;
          padding: var(--spacing-4) var(--spacing-8);
          gap: var(--spacing-4);

          color: var(--color-white);
          @include font-body-s-light;
        }

        img {
          height: 100%;
          width: 100%;
          object-fit: cover;
        }
      }

      .content-wrapper {
        height: 50%;
        width: 100%;
      }

      .summary-wrapper {
        border-top: 1px dashed var(--color-neutral-30);
        padding-top: var(--spacing-16);
        width: 100%;
      }
    }

    // NOTE: min-width is the same as the CARD_WIDTH_BREAKPOINT const in HotelSelectionDynamicScroller.vue file, remember to keep it always equal
    @container hotelCard (min-width: 690px) {
      height: var(--wide-card-height);
      margin: 0;

      .hotel-selection-card-info {
        --card-inside-padding: var(--spacing-16);

        display: grid;
        grid-template-columns: min-content 2fr min-content;

        height: 100%;

        .image-wrapper {
          height: calc(
            var(--wide-card-height) - (var(--card-inside-padding) * 2)
          );
          aspect-ratio: 1/1;
          overflow: hidden;
          border-radius: var(--border-radius-8);

          img {
            object-fit: cover;
          }
        }

        .content-wrapper {
          height: 100%;
          width: auto;
        }

        .summary-wrapper {
          height: 100%;
          width: 100%;
          border-top: none;
          border-left: 1px dashed var(--color-neutral-30);
          padding-left: var(--spacing-16);
        }
      }

      &.sembo-benefits-card-wrapper {
        .sembo-benefits-card {
          gap: var(--spacing-16);

          .sembo-benefits-card-content {
            display: grid;
            grid-template-rows: 1fr 1fr;
            grid-template-columns: 1fr 1fr;
            gap: var(--spacing-12);
            :deep(.sembo-benefit) {
              padding: var(--spacing-12) var(--spacing-16);
            }
          }
        }
      }
    }

    @container hotelCard (min-width: 900px) {
      .hotel-selection-card-info {
        .image-wrapper {
          aspect-ratio: 5/4;
        }
      }
    }
  }
}
</style>
